// ─────────────────────────────────────────────────────────────────────────
// AuditLogSection.tsx
// ─────────────────────────────────────────────────────────────────────────

import { ShieldCheck, MapPin, Camera } from '@phosphor-icons/react';
import { useLanguage } from './LanguageContext';
import { parseGpsString } from './useGeolocation';
import type { GpsString } from './jobSheet.types';

/** Thresholds (meters) used to color-code how much to trust a GPS fix. */
const ACCURACY_GOOD_M = 50;
const ACCURACY_OK_M = 300;

function AccuracyBadge({ accuracy }: { accuracy: number | null }) {
  const { t } = useLanguage();
  if (accuracy == null || Number.isNaN(accuracy)) return null;

  const rounded = accuracy < 10 ? Math.round(accuracy) : Math.round(accuracy / 5) * 5;
  const tier =
    accuracy <= ACCURACY_GOOD_M ? 'good' : accuracy <= ACCURACY_OK_M ? 'ok' : 'poor';
  const classes =
    tier === 'good'
      ? 'border-green-200 bg-green-50 text-green-700'
      : tier === 'ok'
        ? 'border-amber-200 bg-amber-50 text-amber-700'
        : 'border-red-200 bg-red-50 text-red-700';
  const label = tier === 'good' ? t('accuracyGood') : tier === 'ok' ? t('accuracyOk') : t('accuracyPoor');

  return (
    <span
      className={`ml-1 inline-flex items-center rounded border px-1 font-mono text-[9px] font-semibold ${classes}`}
      title={label}
    >
      ±{rounded}{t('metersAbbrev')}
    </span>
  );
}

interface AuditEntryProps {
  label: string;
  active: boolean;
  timeText: string;
  gps: GpsString | null;
  accuracy: number | null;
  /** รูปยืนยันตำแหน่งจริงที่ถ่ายสด (ปัจจุบันมีเฉพาะตอนเริ่มงาน) */
  photoURI?: string | null;
}

function AuditEntry({ label, active, timeText, gps, accuracy, photoURI }: AuditEntryProps) {
  const { t } = useLanguage();
  const coords = parseGpsString(gps);
  return (
    <div className="flex items-start gap-3">
      <div
        className={[
          'mt-1.5 h-2.5 w-2.5 rounded-full transition-colors',
          active ? 'animate-pulse bg-green-500' : 'bg-gray-300',
        ].join(' ')}
      />
      <div className="flex-1">
        <p className="text-xs font-bold text-gray-900">{label}</p>
        <p className={active ? 'mt-0.5 text-xs font-medium text-gray-900' : 'mt-0.5 text-xs text-gray-500'}>
          {timeText}
        </p>
        {coords && (
          <div className="mt-1 rounded border border-brand-100 bg-brand-50 p-1.5">
            <p className="flex flex-wrap items-center gap-1 font-mono text-[10px] text-brand-900">
              <MapPin weight="fill" className="text-[11px] text-brand-600" />
              GPS: {coords.lat}, {coords.lng}
              <AccuracyBadge accuracy={accuracy} />
              <a
                href={`https://www.google.com/maps?q=${coords.lat},${coords.lng}`}
                target="_blank"
                rel="noreferrer"
                className="ml-1 font-semibold text-brand-800 underline"
              >
                ({t('mapLink')})
              </a>
            </p>
          </div>
        )}
        {photoURI && (
          <div className="mt-2 max-w-xs overflow-hidden rounded-xl border border-brand-100 bg-white shadow-sm">
            <div className="flex items-center gap-1.5 border-b border-brand-100 bg-brand-50 px-2.5 py-1.5">
              <Camera weight="fill" className="text-xs text-brand-600" />
              <span className="text-[10px] font-semibold uppercase tracking-wide text-brand-700">
                {t('startJobPhotoLabel')}
              </span>
            </div>
            <img
              src={photoURI}
              alt="Location proof"
              className="h-40 w-full object-cover"
            />
          </div>
        )}
      </div>
    </div>
  );
}

export interface AuditLogSectionProps {
  auditStartTime: string | null;
  auditStartGps: GpsString | null;
  auditStartAccuracy?: number | null;
  /** รูปยืนยันตำแหน่งจริงที่คนขับถ่ายสดตอนกด "เริ่มงาน" */
  auditStartPhotoURI?: string | null;
  auditEndTime: string | null;
  auditEndGps: GpsString | null;
  auditEndAccuracy?: number | null;
}

export default function AuditLogSection({
  auditStartTime,
  auditStartGps,
  auditStartAccuracy = null,
  auditStartPhotoURI = null,
  auditEndTime,
  auditEndGps,
  auditEndAccuracy = null,
}: AuditLogSectionProps) {
  const { t } = useLanguage();

  return (
    <section className="overflow-hidden rounded-xl border border-brand-100 bg-white shadow-sm">
      <div className="info-card-header">
        <ShieldCheck className="text-xl text-brand-800" />
        <h3 className="text-sm font-semibold uppercase tracking-wide text-brand-900">
          {t('auditLogTitle')}
        </h3>
      </div>
      <div className="space-y-3 p-4">
        <AuditEntry
          label={t('startJobLog')}
          active={Boolean(auditStartTime)}
          timeText={auditStartTime ?? t('awaitingStart')}
          gps={auditStartGps}
          accuracy={auditStartAccuracy}
          photoURI={auditStartPhotoURI}
        />
        <div className="my-2 border-t border-gray-100" />
        <AuditEntry
          label={t('endJobLog')}
          active={Boolean(auditEndTime)}
          timeText={auditEndTime ?? t('awaitingEnd')}
          gps={auditEndGps}
          accuracy={auditEndAccuracy}
        />
      </div>
    </section>
  );
}
